// Meta Pixel + GA4 tracking loader
// Loaded once on app mount. Pixel/GA IDs are pulled from /api/settings/tracking.

const STATE = {
  metaLoaded: false,
  ga4Loaded: false,
  metaId: "",
  ga4Id: "",
};

function injectScript(src, attrs = {}) {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.async = true;
    s.src = src;
    Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

// ---------- Meta Pixel ----------
export function initMetaPixel(pixelId) {
  if (!pixelId || STATE.metaLoaded) return;
  if (typeof window === "undefined") return;

  // Standard Meta snippet, adapted to avoid eval / inline insertion via document.write.
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");

  window.fbq("init", pixelId);
  window.fbq("track", "PageView");

  STATE.metaLoaded = true;
  STATE.metaId = pixelId;
}

// ---------- GA4 ----------
export function initGA4(measurementId) {
  if (!measurementId || STATE.ga4Loaded) return;
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", measurementId, { send_page_view: true });

  injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`).catch(() => {
    /* silent */
  });

  STATE.ga4Loaded = true;
  STATE.ga4Id = measurementId;
}

// ---------- Event helpers ----------
/**
 * Fire a Lead/generate_lead event on both Meta and GA4.
 * Call AFTER successful form submission.
 * @param {object} lead - lead payload
 * @param {object} opts - { value: number, currency: string }
 */
export function trackLead(lead = {}, opts = {}) {
  const params = {
    currency: opts.currency || "INR",
    value: typeof opts.value === "number" ? opts.value : 0,
    // useful custom params (Meta + GA4 both accept arbitrary keys)
    wedding_location: lead.wedding_location || "",
    wedding_date: lead.wedding_date || "",
    guest_count: lead.guest_count || "",
    budget_range: lead.budget_range || "",
    source: lead.source || "website",
  };

  try {
    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("track", "Lead", params);
    }
  } catch (e) { /* silent */ }

  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "generate_lead", params);
    }
  } catch (e) { /* silent */ }
}

/**
 * Generic event helper for buttons (e.g. custom events not covered by standard Meta events).
 */
export function trackEvent(name, params = {}) {
  try {
    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("trackCustom", name, params);
    }
  } catch (e) { /* silent */ }
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", name, params);
    }
  } catch (e) { /* silent */ }
}

/**
 * Fire the Meta STANDARD `Contact` event + the GA4 equivalent.
 * Call when a visitor taps a contact channel (phone, email, WhatsApp).
 *
 * @param {string} method - "whatsapp" | "phone" | "email"
 * @param {object} extra  - optional params (location of the click, etc.)
 */
export function trackContact(method, extra = {}) {
  const params = {
    content_name: "wedding_planning_enquiry",
    content_category: "contact_intent",
    contact_method: method,
    ...extra,
  };
  try {
    if (typeof window !== "undefined" && window.fbq) {
      // Meta standard event — uses 'track', not 'trackCustom'.
      window.fbq("track", "Contact", params);
    }
  } catch (e) { /* silent */ }
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "contact", params);
    }
  } catch (e) { /* silent */ }
}

/**
 * Fire the Meta STANDARD `ViewContent` event + the GA4 `view_item` event.
 * Call when the visitor scrolls a key section (e.g. Packages) into view.
 *
 * Use the `dedupeKey` to ensure it fires once per session per content area —
 * IntersectionObserver tends to retrigger on scroll back-and-forth.
 */
const _viewContentFired = new Set();
export function trackViewContent(params = {}, dedupeKey) {
  if (dedupeKey && _viewContentFired.has(dedupeKey)) return;
  if (dedupeKey) _viewContentFired.add(dedupeKey);

  const payload = {
    content_name: params.content_name || "planning_experiences",
    content_category: params.content_category || "packages",
    content_type: "product_group",
    ...params,
  };
  try {
    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("track", "ViewContent", payload);
    }
  } catch (e) { /* silent */ }
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "view_item", payload);
    }
  } catch (e) { /* silent */ }
}

export function getTrackingState() {
  return { ...STATE };
}
